import { useNavigation, useRoute } from '@react-navigation/native';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { useFormikContext } from 'formik';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import { ModalHeader } from '@components/Headers/ModalHeader';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { WatchedPaymentElement } from '@components/Payments/WatchedPaymentElement';
import { removeProjectLimitSnackBarNextTime } from '@components/Projects/ProjectForm.web';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { PlanItemWeb } from '@components/shared/PlanItemWeb';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  GetTeamBillingDocument,
  GetTeamDocument,
  LocalFile,
  Price,
  Product,
  TeamRole,
  User,
  useEditStripeSubscriptionMutation,
  useListProductsQuery,
  useListTeamsQuery,
  useRemoveTeamMemberMutation,
  useCreateTeamMemberInvitationMutation,
  useUpdateTeamMembersMutation,
  useUpdatePaymentMethodMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { FormValues } from '@navigation/payments/create-subscription-stack';
import { BillingCycle, PlanName } from '@root/types';
import { MOBILE_PLAN_NAME, WEB_PLAN_NAME } from '@src/constants/constants';
import theme from '@themes/theme';
import {
  calculateAnnualSavingPercentage,
  calculateSubtotalCost,
  maskEmail,
} from '@utils/teams/pricing';
import { splitTeamMemberChanges } from '@utils/teams/splitTeamMemberChanges';
import { formatCardExpiration, isCardExpired } from '@utils/teams/subscription';

// this page is for web edit subscription
interface EditSubscriptionProps {
  upgradeCTA?: boolean;
}

export const EditSubscription: React.FC<EditSubscriptionProps> = ({
  upgradeCTA = false,
}) => {
  enum viewType {
    MainPage = 'Main Page',
    EditPlan = 'Edit Plan',
    EditMember = 'Edit Member',
    EditPaymentMethod = 'Edit Payment Method',
  }
  type UserWithRole = User & { role: TeamRole };

  const {
    setUpgradeTeamPlanOpen,
    userInvited,
    setUserInvited,
    editSubscriptionAutoAnnual,
    setEditSubscriptionAutoAnnual,
  } = useWebDrawer();

  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { users, plan, priceId, billingCycle, name, logo } = values;
  const route = useRoute();
  const { teamUUId } = route?.params || { teamUUId: '' };

  const { team } = useTeamFromQuery({
    teamUUId,
  });

  // Billing data is split out of listTeams (TA-9873) and fetched separately
  const { billingByTeamId, loading: billingLoading } = useTeamBillingFromQuery({
    includePending: true,
  });
  const teamBilling = team?.id ? billingByTeamId[team.id] : undefined;
  const subscriptionDetails = teamBilling?.subscriptionDetails;

  const [teamId, setTeamId] = useState<string>('');

  useEffect(() => {
    team?.id && setTeamId(team?.id);
  }, [team]);

  const stripe = useStripe();

  const [loading, setLoading] = useState(false);
  const [pageViewType, setViewType] = useState<viewType>(viewType.MainPage);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [selectedMemberRole, setSelectedMemberRole] = useState<TeamRole>();
  const [selectedMember, setSelectedMember] = useState<User>();
  const [editRoleModalOpen, setEditRoleModalOpen] = useState<boolean>(false);
  const [isDeleteMemberModalVisible, setDeleteMemberModalVisible] =
    useState<boolean>(false);

  const { me } = useMe();
  const { data } = useListTeamsQuery({ fetchPolicy: 'cache-only' });
  const { listTeams } = data || {};
  const navigator = useNavigation();
  const { t } = useTranslation();
  const { data: listProductsData } = useListProductsQuery();

  const TeamMemberRoles = [TeamRole.Admin, TeamRole.Member];

  const [updateTeamMembers] = useUpdateTeamMembersMutation();
  const [createTeamMemberInvitation] = useCreateTeamMemberInvitationMutation();

  const [
    editStripeSubscription,
    { data: editStripeSubscriptionData, error: editStripeSubscriptionError },
  ] = useEditStripeSubscriptionMutation({
    refetchQueries: [
      { query: GetTeamBillingDocument, variables: { includePending: true } },
    ],
  });

  useEffect(() => {
    if (!editStripeSubscriptionData) return;
    setLoading(false);
    removeProjectLimitSnackBarNextTime(me?.id, '0');
    removeProjectLimitSnackBarNextTime(me?.id, teamId);
  }, [editStripeSubscriptionData]);

  useEffect(() => {
    if (!editStripeSubscriptionError) return;
    setLoading(false);
  }, [editStripeSubscriptionError]);

  const [
    updatePaymentMethod,
    { data: updatePaymentMethodData, error: updatePaymentMethodError },
  ] = useUpdatePaymentMethodMutation({
    refetchQueries: [
      { query: GetTeamBillingDocument, variables: { includePending: true } },
    ],
  });

  useEffect(() => {
    if (!updatePaymentMethodData) return;
    setLoading(false);
  }, [updatePaymentMethodData]);

  useEffect(() => {
    if (!updatePaymentMethodError) return;
    setLoading(false);
  }, [updatePaymentMethodError]);

  const { listProducts } = listProductsData || {};
  const elements = useElements();

  const [showError, setShowError] = useState(false);
  const [payment, setPayment] = useState(elements?.getElement('payment'));

  const personalTeam = listTeams?.find((t) => t?.personal);

  type PlanWithPrices = {
    [id: Product['id']]: {
      perMonth: Price['amount'];
      perYear: Price['amount'];
    };
  };
  const stripeOptions: StripePaymentElementOptions = {};

  const [removeTeamMember] = useRemoveTeamMemberMutation();

  // convert Stripe products in to an easier to use format
  const plans: PlanWithPrices =
    listProducts?.reduce((acc, product) => {
      const prices = {
        perMonth:
          product?.prices?.find((price) => price?.interval === 'month')
            ?.amount || 0,
        perYear:
          product?.prices?.find((price) => price?.interval === 'year')
            ?.amount || 0,
      };
      return { ...acc, [product['id']]: prices };
    }, {}) || {};

  const isPersonal = team?.personal ?? false;

  const filteredProducts = listProducts?.filter((plan) =>
    plan?.name
      .toLowerCase()
      .includes(Platform.OS === 'web' ? WEB_PLAN_NAME : MOBILE_PLAN_NAME)
  );

  const [displayPlan, setDisplayPlan] = React.useState(
    !subscriptionDetails && (filteredProducts?.length ?? 0) > 0
      ? filteredProducts![0].id
      : plan
  );

  const usersAndMe = () => {
    return users;
  };

  const sortedUsers = [...(usersAndMe() || [])]?.sort((u) =>
    u?.role !== TeamRole?.Owner ? 1 : -1
  );

  const setPlan = (plan: PlanName) => {
    const product = listProducts?.find((p) => p?.id === plan);
    setFieldValue('plan', plan);
    product && setFieldValue('planName', product?.name);

    const price = product?.prices?.find(
      (price) => price?.interval === billingCycle
    );
    price && setFieldValue('priceId', price?.externalId);
    price && setFieldValue('price', subtotalCost());

    if (!personalTeam) return;

    if (!product?.allowMembers) {
      setFieldValue('teamId', personalTeam?.id);
    } else {
      setFieldValue('teamId', null);
    }
  };

  useEffect(() => {
    if (userInvited) {
      setViewType(viewType.EditMember);
      setUserInvited(false);
    }
  }, [userInvited]);

  useEffect(() => {
    if (!editSubscriptionAutoAnnual) return;
    if (!priceId || !teamId || !selectedProduct) return;

    if (subscriptionDetails?.subscriptionInterval === 'year') {
      setEditSubscriptionAutoAnnual(false);
      setUpgradeTeamPlanOpen(false);
      return;
    }

    if (billingCycle !== 'year') {
      setFieldValue('billingCycle', 'year');
      const yearlyPrice = selectedProduct?.prices?.find(
        (price) => price?.interval === 'year'
      );
      if (yearlyPrice) {
        setFieldValue('priceId', yearlyPrice?.externalId);
      }
      return; // Wait for next render with updated values
    }

    const submitAnnualSubscription = async () => {
      setLoading(true);
      try {
        const { data: subscriptionData } = await editStripeSubscription({
          variables: {
            stripeSubscriptionAttributes: {
              name: plan || '',
              teamId: teamId.toString(),
              quantity: users.length,
              priceId: priceId,
            },
          },
          refetchQueries: [
            {
              query: GetTeamBillingDocument,
              variables: { includePending: true },
            },
            ...(teamUUId
              ? [
                  {
                    query: GetTeamDocument,
                    variables: { id: teamUUId, includePending: true },
                  },
                ]
              : []),
          ],
        });

        const { editStripeSubscription: subscriptionResponse } =
          subscriptionData || {};

        if (!subscriptionResponse) {
          showSubscripeFail();
          setEditSubscriptionAutoAnnual(false);
          setLoading(false);
          return;
        }

        setEditSubscriptionAutoAnnual(false);
        setUpgradeTeamPlanOpen(false);
        setLoading(false);
      } catch {
        showSubscripeFail();
        setEditSubscriptionAutoAnnual(false);
        setLoading(false);
      }
    };

    submitAnnualSubscription();
  }, [
    editSubscriptionAutoAnnual,
    priceId,
    teamId,
    selectedProduct,
    billingCycle,
    subscriptionDetails?.subscriptionInterval,
  ]);

  const setBillingCycle = (billingCycle: BillingCycle) => {
    setFieldValue('billingCycle', billingCycle);
  };

  const selectedProduct = listProducts?.find((p) => p?.id === displayPlan);

  const subtotalCost = () =>
    calculateSubtotalCost({
      plans,
      displayPlan,
      billingCycle,
      memberCount: users.length,
      allowMembers: selectedProduct?.allowMembers ?? false,
    });
  const subtotalValue = subtotalCost();

  const subtotalLabel = `$${(subtotalValue / 100)?.toFixed(2)}`;

  const previousMembersCost = () => {
    const planCost =
      billingCycle === 'year'
        ? plans?.[displayPlan]?.perYear
        : plans?.[displayPlan]?.perMonth;
    return `$${(Math.ceil(planCost * (team?.usersCount ?? 1)) / 100)?.toFixed(
      2
    )}`;
  };

  const newMembersCost = () => {
    const planCost =
      billingCycle === 'year'
        ? plans?.[displayPlan]?.perYear
        : plans?.[displayPlan]?.perMonth;

    return `$${(Math.ceil(planCost * users.length) / 100)?.toFixed(2)}`;
  };
  const originalCost = () => {
    if (!listProducts?.length || !displayPlan) return 0;
    const multiplier =
      displayPlan === MOBILE_PLAN_NAME ? 1 : Math.max(users.length, 1);

    const selectedPlan = plans?.[displayPlan];
    return billingCycle === 'year'
      ? selectedPlan?.perMonth * 12 * multiplier
      : selectedPlan?.perMonth * multiplier;
  };

  const teamIcon = () => {
    if (isPersonal) {
      return (
        <Box
          width={24}
          height={24}
          borderRadius='xxs'
          backgroundColor='greenSecondary'
          justifyContent='center'
          alignItems='center'>
          <Icon variant='s' name='Office' color='white' />
        </Box>
      );
    } else {
      return <Icon name='TT24' variant='l' color='textPrimary' />;
    }
  };

  const originalCostLabel = `$${(originalCost() / 100)?.toFixed(2)}`;

  const showSubscripeFail = () => {
    setShowError(true);
  };

  useEffect(() => {
    const product = listProducts?.find((p) => p.id === displayPlan);
    const price = product?.prices.find(
      (price) => price.interval === billingCycle
    );
    price && setFieldValue('priceId', price.externalId);
    price && setFieldValue('price', subtotalCost());
  }, [billingCycle, subtotalValue]);

  useEffect(() => {
    if (!payment) return;

    payment.on('change', (e) => {
      setFieldValue('paymentComplete', e.complete);
    });

    return () => {
      // Stripe documentation indicated this should be done, even though TS is yelling about it
      (payment as any)?.removeAllListeners();
    };
  }, [payment]);

  useEffect(() => {
    if (Boolean(team?.id) && !isPersonal) {
      setFieldValue('name', team?.name);
      setFieldValue('teamId', team?.id);
    }
  }, [plan]);

  useEffect(() => {
    if (!filteredProducts) return;

    const product = filteredProducts[0];
    product && setDisplayPlan(product.id);
    product && setFieldValue('plan', product.id);
    product && setFieldValue('planName', product.name);
  }, [filteredProducts?.length]);

  useEffect(() => {
    if (subscriptionDetails?.subscriptionInterval) {
      setBillingCycle(
        subscriptionDetails?.subscriptionInterval === 'year' ? 'year' : 'month'
      );
    }
  }, [pageViewType]);

  const percentageSavedWeb = (product: Product) =>
    calculateAnnualSavingPercentage(plans?.[product.id]);

  let upToSave = 20;
  filteredProducts?.forEach((p) => {
    const saved = percentageSavedWeb(p) ?? 0;
    if (upToSave <= saved) upToSave = saved;
  });

  const removeMemberFromTeam = (userId: string) => {
    setFieldValue(
      'users',
      users.filter((u) => u.id !== userId)
    );
  };

  const changeBillingFrequency = async () => {
    setLoading(true);
    const { data: subscriptionData } = await editStripeSubscription({
      variables: {
        stripeSubscriptionAttributes: {
          name: plan || '',
          teamId: teamId.toString(),
          quantity: users.length,
          priceId: priceId,
        },
      },
      refetchQueries: [
        {
          query: GetTeamBillingDocument,
          variables: { includePending: true },
        },
        ...(teamUUId
          ? [
              {
                query: GetTeamDocument,
                variables: { id: teamUUId, includePending: true },
              },
            ]
          : []),
      ],
    });
    const { editStripeSubscription: subscriptionResponse } =
      subscriptionData || {};

    if (!subscriptionResponse) {
      showSubscripeFail();
      return;
    } else {
      if (!subscriptionResponse) {
        showSubscripeFail();
        return;
      }
    }
    setLoading(false);
    setViewType(viewType.MainPage);
  };
  // Pass null when expiry fields are missing — a saved card with unknown
  // expiry is treated as expired (matches CreateSubscription)
  const isSavedPaymentMethodExpired = isCardExpired(
    subscriptionDetails?.paymentCardExpMonth &&
      subscriptionDetails?.paymentCardExpYear
      ? `${subscriptionDetails.paymentCardExpMonth}/` +
          `${subscriptionDetails.paymentCardExpYear}`
      : null
  );

  const expiration = formatCardExpiration(
    subscriptionDetails?.paymentCardExpMonth,
    subscriptionDetails?.paymentCardExpYear,
    isSavedPaymentMethodExpired
  );

  const hasSavedPaymentMethod = subscriptionDetails?.paymentCardLast4;

  const [useNewPaymentMethod, setUseNewPaymentMethod] = useState(
    teamId === undefined ||
      teamId === '' ||
      !hasSavedPaymentMethod ||
      isSavedPaymentMethodExpired
  );

  // Billing now arrives async (separate getTeamBilling request) — re-derive
  // the default payment method selection once it loads
  useEffect(() => {
    setUseNewPaymentMethod(
      teamId === undefined ||
        teamId === '' ||
        !hasSavedPaymentMethod ||
        isSavedPaymentMethodExpired
    );
  }, [teamId, hasSavedPaymentMethod, isSavedPaymentMethodExpired]);

  const changePaymentMethod = async () => {
    // Upgrade CTA with the saved card selected — there is no new payment
    // method to tokenize; upgrade the subscription directly with the
    // existing payment method. (The EditPaymentMethod view always collects
    // a new card and must keep the tokenize path below.)
    if (
      upgradeCTA &&
      pageViewType === viewType.MainPage &&
      !useNewPaymentMethod &&
      hasSavedPaymentMethod
    ) {
      if (!priceId || !teamId) {
        showSubscripeFail();
        return;
      }
      setLoading(true);
      try {
        const { data: subscriptionData } = await editStripeSubscription({
          variables: {
            stripeSubscriptionAttributes: {
              name: plan || '',
              teamId: teamId.toString(),
              quantity: users.length,
              priceId: priceId,
            },
          },
          refetchQueries: [
            {
              query: GetTeamBillingDocument,
              variables: { includePending: true },
            },
            ...(teamUUId
              ? [
                  {
                    query: GetTeamDocument,
                    variables: { id: teamUUId, includePending: true },
                  },
                ]
              : []),
          ],
        });

        if (!subscriptionData?.editStripeSubscription) {
          showSubscripeFail();
          setLoading(false);
          return;
        }

        setLoading(false);
        setUpgradeTeamPlanOpen(false);
      } catch {
        showSubscripeFail();
        setLoading(false);
      }
      return;
    }

    if (stripe && elements) {
      setLoading(true);

      const res0 = await elements.submit();

      if (
        subscriptionDetails?.paymentCardLast4 !== '' &&
        !values.paymentComplete
      ) {
        setLoading(false);
        return;
      }
      if (!res0.error) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          elements: elements,
        });

        if (paymentMethod) {
          await updatePaymentMethod({
            variables: {
              teamId: teamId,
              paymentMethodToken: paymentMethod?.id ?? '',
            },
            refetchQueries: [
              {
                query: GetTeamBillingDocument,
                variables: { includePending: true },
              },
              ...(teamUUId
                ? [
                    {
                      query: GetTeamDocument,
                      variables: { id: teamUUId, includePending: true },
                    },
                  ]
                : []),
            ],
          });
        }

        setLoading(false);

        if (error) {
          showSubscripeFail();
        }
        setViewType(viewType.MainPage);
      } else {
        showSubscripeFail();
      }
    }
  };
  const changeSeat = async () => {
    setLoading(true);

    const membersNeedToRemove = team?.users.filter(
      (tu) => !users.some((nu) => nu.id === tu.id)
    );

    const membersNeedToUpdate = users.filter(
      (nu) =>
        !team?.users.some(
          (tu) => nu.id === tu.id && tu.teamRoles?.includes(nu.role)
        )
    );

    // New people are invited rather than added; the backend bills their seat
    // when they join (TA-11187).
    const { roleUpdates, invitations } = splitTeamMemberChanges(
      membersNeedToUpdate,
      team?.users.map((tu) => tu.id) ?? []
    );
    const teamRefetchQueries = teamUUId
      ? [
          {
            query: GetTeamDocument,
            variables: { id: teamUUId, includePending: true },
          },
          'getTeam' as const,
        ]
      : [];

    setLoading(true);
    await Promise.all([
      ...(membersNeedToRemove
        ? membersNeedToRemove.map(({ id: userId }) =>
            removeTeamMember({
              variables: {
                id: teamId,
                userId,
              },
              refetchQueries: teamUUId
                ? [
                    {
                      query: GetTeamDocument,
                      variables: { id: teamUUId, includePending: true },
                    },
                  ]
                : [],
            })
          )
        : []),
      updateTeamMembers({
        variables: {
          teamId,
          attributes: roleUpdates.filter((t) => t.role !== TeamRole.Owner),
        },
        onError: () => {
          showSubscripeFail();
          return;
        },
        refetchQueries: [
          {
            query: GetTeamBillingDocument,
            variables: { includePending: true },
          },
          ...teamRefetchQueries,
        ],
      }),
      ...(invitations.length
        ? [
            createTeamMemberInvitation({
              variables: { id: teamId, teamMembers: invitations },
              onError: () => {
                showSubscripeFail();
              },
              refetchQueries: teamRefetchQueries,
            }),
          ]
        : []),
    ]);
    setLoading(false);
    setViewType(viewType.MainPage);
  };

  const editPanel = (
    node: ReactNode,
    doEdit: () => void,
    disable = false,
    paddingBottom: keyof typeof theme.spacing = 'm'
  ) => {
    return (
      <Box
        flexDirection='row'
        alignItems='center'
        borderWidth={1}
        borderRadius='xs'
        borderColor='grey02'
        px='m'
        pt='m'
        pb={paddingBottom}
        style={{
          shadowOpacity: 0.05,
          shadowOffset: { width: 0, height: 0.25 },
        }}>
        <Box flex={1} marginRight='m' alignItems='flex-start'>
          {node}
        </Box>
        {!disable && (
          <TouchableOpacity
            accessibilityLabel={t('shared:edit')}
            onPress={doEdit}>
            <Box
              backgroundColor='greenSecondaryMild'
              width={30}
              height={24}
              alignItems='center'
              borderRadius='xxs'
              justifyContent='center'>
              <Text color='greenSecondary' variant='metadata'>
                {t('shared:edit')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}
      </Box>
    );
  };
  const subTitle1 = (
    xtitle: string,
    marginBottom: keyof typeof theme.spacing = 'xs'
  ) => {
    return (
      <Text
        marginBottom={marginBottom}
        variant='labelLarge'
        color='textPrimary'>
        {xtitle}
      </Text>
    );
  };

  const subTitle2 = (
    xtitle: string,
    xcolor: keyof typeof theme.colors = 'textSecondary',
    marginBottom: keyof typeof theme.spacing = 'm'
  ) => (
    <Text variant='webBodySecondary' mb={marginBottom} color={xcolor}>
      {xtitle}
    </Text>
  );

  const updateTeamMemberRole = (userId: string, role: TeamRole) => {
    const newUsers: UserWithRole[] = [];
    users.forEach((u) => {
      if (u.id === userId) {
        u.role = role;
      }
      newUsers.push(u);
    });
    setFieldValue('users', newUsers);
  };

  const cancelEditPlanPage = () => {
    setPlan(subscriptionDetails?.stripeProductId);
    setBillingCycle('year' as BillingCycle);
    setViewType(viewType.MainPage);
  };

  const cancelEditMemberPage = () => {
    setFieldValue(
      'users',
      team?.users.map((u) => ({
        ...u,
        role:
          u.teamRoles && u.teamRoles.length > 0
            ? u.teamRoles[0]
            : TeamRole.Member,
      }))
    );
    setBillingCycle('year' as BillingCycle);
    setViewType(viewType.MainPage);
  };
  const cancelPaymentMethodPage = () => {
    setViewType(viewType.MainPage);
    setBillingCycle('year' as BillingCycle);
  };

  const changed = () => {
    if (team) {
      if (!subscriptionDetails && plan) return true;
      if (
        subscriptionDetails &&
        subscriptionDetails.subscriptionInterval !== plan
      )
        return true;
      if (team.users.length != users.length) return true;
      const oldUsersIds = team.users.map((u) => u.id);

      for (const a of users) {
        if (!oldUsersIds.includes(a.id)) return true;
      }
    }
    return false;
  };

  // Billing arrives in a separate getTeamBilling request (TA-9873) — show a
  // loading state until it lands
  if (!!teamUUId && billingLoading && !teamBilling) {
    return (
      <Box
        flex={1}
        justifyContent='center'
        alignItems='center'
        backgroundColor='white'
        padding='l'>
        <ActivityIndicator testID='BillingActivityIndicator' />
      </Box>
    );
  }

  return (
    <>
      {isDeleteMemberModalVisible && (
        <ConfirmModal
          showModal={isDeleteMemberModalVisible}
          onClose={() => setDeleteMemberModalVisible(false)}
          onPress={() => {
            setDeleteMemberModalVisible(false);
            removeMemberFromTeam(selectedMember?.id ?? '0');
          }}
          buttonText={t('models:teams.removeTeamMember.remove')}
          title={t('models:teams.removeTeamMember.title')}
          message={t('models:teams.removeTeamMember.message', {
            member: selectedMember?.name ?? '',
          })}
        />
      )}

      {showError && (
        <ConfirmModal
          isAlert={true}
          showModal={showError}
          onClose={() => setShowError(false)}
          onPress={() => setShowError(false)}
          buttonText='     OK     '
          title={t('models:teams.subscriptions.failureAlert.title')}
          message={t('models:teams.subscriptions.failureAlert.message')}
        />
      )}
      <Box flex={1} backgroundColor='white'>
        <ScrollView
          style={{ flex: 1, backgroundColor: theme.colors.white }}
          contentContainerStyle={{ paddingBottom: theme.spacing.m }}
          showsVerticalScrollIndicator={false}>
          <Box
            backgroundColor='white'
            maxWidth={560}
            paddingStart='m'
            paddingTop='m'>
            <Box flexDirection='row' marginBottom='s'>
              <Box flex={1} flexDirection='row'>
                {upgradeCTA ? (
                  <Text variant='heading2' color='textPrimary' textAlign='left'>
                    {t('models:profile.subscriptionPlans.subscriptionDetails')}
                  </Text>
                ) : (
                  <Text variant='heading2' color='textPrimary' textAlign='left'>
                    {t('models:profile.subscriptionPlans.editSubscription')}
                  </Text>
                )}
              </Box>
              <Box alignItems='flex-end'>
                <Box flexDirection='row' accessibilityLabel='Close'>
                  <Icon
                    name='X'
                    onPress={() => {
                      switch (pageViewType) {
                        case viewType.MainPage:
                          setUpgradeTeamPlanOpen(false);
                          break;
                        case viewType.EditPlan:
                          cancelEditPlanPage();
                          break;
                        case viewType.EditMember:
                          cancelEditMemberPage();
                          break;
                        case viewType.EditPaymentMethod:
                          cancelPaymentMethodPage();
                          break;
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {pageViewType === viewType.MainPage && (
              <>
                <Box my='m'>
                  {subTitle1('Select Team')}
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='center'
                    borderColor='grey03'
                    backgroundColor='grey01'
                    borderWidth={1}
                    borderRadius='xs'
                    height={48}
                    mt='xs'
                    mb='m'>
                    {team?.avatarFile && (
                      <CachedImage
                        image={team.avatarFile as unknown as LocalFile}
                        width={32}
                        height={32}
                        borderRadius={theme.spacing.xs}
                      />
                    )}
                    {!team?.avatarFile && name && logo && (
                      <CachedImage
                        image={logo as unknown as LocalFile}
                        height={32}
                        width={32}
                        borderRadius={theme.spacing.xs}
                      />
                    )}
                    {!team?.avatarFile && !logo && teamIcon()}
                    <Text
                      mx='xs'
                      variant='labelSmall'
                      color='textPrimary'
                      numberOfLines={1}>
                      {team?.name || name}
                    </Text>
                  </Box>
                </Box>

                <Box mb='l'>
                  {subTitle1(
                    t('models:teams.subscriptions.chooseYourBillingFrequency')
                  )}
                  <Box flexDirection='row'>
                    {subTitle2(
                      t('models:payments.annualSavings', {
                        savingPercentage: upToSave,
                      }) + ' ',
                      'greenSecondary'
                    )}
                    {subTitle2(
                      t('models:teams.subscriptions.whenYouSubscribeAnnually')
                    )}
                  </Box>

                  {editPanel(
                    <Box
                      height={42}
                      flexDirection='row'
                      alignContent='flex-start'
                      alignItems='center'>
                      <Icon
                        name='Star'
                        backgroundColor='purple'
                        color='background'
                        marginRight='xs'
                      />
                      <Box>
                        <Box flexDirection='row' alignItems='center'>
                          <Text variant='labelEmphasized' color='textPrimary'>
                            {(subscriptionDetails?.subscriptionPlanName ??
                              'Team') + ' Plan'}
                          </Text>
                        </Box>
                        {subscriptionDetails?.subscriptionInterval ===
                        'month' ? (
                          <Text variant='webBodySecondary' color='grey04'>
                            Billed Monthly
                          </Text>
                        ) : (
                          <Text variant='webBodySecondary' color='grey04'>
                            Billed Annually
                          </Text>
                        )}
                      </Box>
                    </Box>,
                    () => {
                      setConfirmEdit(false);
                      setViewType(viewType.EditPlan);
                    },
                    subscriptionDetails?.subscriptionInterval === 'year'
                  )}
                </Box>
                <Box my='m'>
                  {subTitle1(t('models:teams.subscriptions.teamMembers'))}
                  {subTitle2(
                    'Your current subscription includes ' +
                      (team?.usersCount ?? 1).toString() +
                      ' team member' +
                      ((team?.usersCount ?? 1) > 1 ? 's' : '')
                  )}
                  {editPanel(
                    <>
                      {!isPersonal && sortedUsers && sortedUsers.length > 0 && (
                        <Box
                          flexDirection='column'
                          alignItems='flex-start'
                          justifyContent='center'>
                          <Box
                            flex={1}
                            flexDirection='row'
                            alignItems='flex-start'
                            justifyContent='flex-start'
                            flexWrap='wrap'>
                            {sortedUsers?.map((user) => {
                              if (!user) return;
                              const {
                                id: memberId,
                                firstName,
                                name,
                                avatar,
                              } = user;

                              return (
                                <Box
                                  key={memberId}
                                  marginRight='m'
                                  alignItems='center'>
                                  <Avatar
                                    disableAvatarPress={true}
                                    size='large'
                                    label={name}
                                    avatar={avatar}
                                  />
                                  <Text
                                    variant='webSmall'
                                    textAlign='center'
                                    style={{
                                      width: 56,
                                      marginTop:
                                        theme.spacing.xs + theme.spacing.xxxs,
                                    }}
                                    marginBottom='m'
                                    numberOfLines={1}>
                                    {firstName}
                                  </Text>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </>,
                    () => {
                      setConfirmEdit(false);
                      setViewType(viewType.EditMember);
                    },
                    false,
                    'none'
                  )}
                </Box>

                {
                  <Box my='m'>
                    {subTitle1(t('models:teams.subscriptions.paymentMethod'))}
                    {upgradeCTA
                      ? subTitle2(t('models:teams.subscriptions.upgradeInfo'))
                      : subTitle2(
                          t(
                            'models:teams.subscriptions.thisPaymentMethodWillBeBilledForYourSubscription'
                          )
                        )}
                    {!upgradeCTA &&
                      team &&
                      editPanel(
                        <>
                          {subscriptionDetails &&
                            (
                              subscriptionDetails.paymentCardBrand ?? ''
                            ).includes('@') && (
                              <Box
                                flexDirection='row'
                                justifyContent='center'
                                alignItems='center'>
                                <Icon
                                  name='LinkPayment'
                                  variant='l'
                                  color='white'
                                />
                                <Text
                                  variant='labelSmall'
                                  color='textSecondary'
                                  ml='xxs'>
                                  {`Link  ${maskEmail(
                                    subscriptionDetails.paymentCardBrand ?? ''
                                  )}`}
                                </Text>
                                <Box flex={1} />
                              </Box>
                            )}
                          {/*stripe payment method*/}
                          {subscriptionDetails?.paymentCardLast4 && (
                            <Box
                              flexDirection='row'
                              justifyContent='center'
                              alignItems='center'>
                              <Icon name='Visa' variant='xxl' color='white' />
                              <Text
                                variant='labelSmall'
                                color='textSecondary'
                                ml='m'>
                                **** **** ****{' '}
                                {subscriptionDetails?.paymentCardLast4}
                              </Text>
                            </Box>
                          )}
                          {subscriptionDetails &&
                            !subscriptionDetails?.paymentCardLast4 && (
                              <Box
                                flexDirection='row'
                                justifyContent='center'
                                alignItems='center'>
                                <Text
                                  variant='labelSmall'
                                  color='textSecondary'>
                                  Payment method not found.
                                </Text>
                              </Box>
                            )}
                        </>,
                        () => {
                          setConfirmEdit(false);
                          setViewType(viewType.EditPaymentMethod);
                        }
                      )}
                  </Box>
                }
              </>
            )}
            {pageViewType === viewType.EditPlan && (
              <>
                <Box mb='l'>
                  {subTitle1(
                    t('models:teams.subscriptions.chooseYourBillingFrequency')
                  )}
                  <Box flexDirection='row'>
                    {subTitle2(
                      t('models:payments.annualSavings', {
                        savingPercentage: upToSave,
                      }) + ' ',
                      'greenSecondary'
                    )}
                    {subTitle2(
                      t('models:teams.subscriptions.whenYouSubscribeAnnually')
                    )}
                  </Box>
                  <Box flex={1}>
                    {filteredProducts?.map((product) => {
                      return (
                        <PlanItemWeb
                          key={'2' + product.id}
                          product={product}
                          isMonthly={false}
                          isSelected={
                            displayPlan === product.id &&
                            billingCycle === 'year'
                          }
                          onPress={() => {
                            if (
                              displayPlan !== product.id ||
                              billingCycle !== 'year'
                            ) {
                              setDisplayPlan(product.id);
                              setPlan(product.id);
                              setBillingCycle('year');
                            }
                          }}
                          percentageSaved={percentageSavedWeb(product)}
                          showBillingLabel={product.allowMembers}
                        />
                      );
                    })}
                    {filteredProducts?.map((product) => {
                      return (
                        <PlanItemWeb
                          key={'1' + product.id}
                          product={product}
                          isMonthly={true}
                          isSelected={
                            displayPlan === product.id &&
                            billingCycle === 'month'
                          }
                          disabled={
                            subscriptionDetails?.subscriptionInterval === 'year'
                          }
                          onPress={() => {
                            if (
                              displayPlan !== product.id ||
                              billingCycle !== 'month'
                            ) {
                              setDisplayPlan(product.id);
                              setPlan(product.id);
                              setBillingCycle('month');
                            }
                          }}
                          showBillingLabel={product.allowMembers}
                        />
                      );
                    })}
                  </Box>
                </Box>

                <Box
                  mb='l'
                  flexDirection='row'
                  flex={1}
                  style={{ paddingVertical: 32 }}
                  px='l'
                  borderColor='grey02'
                  borderWidth={1}
                  backgroundColor='grey01'
                  accessibilityLabel={t('models:teams.subscriptions.subtotal')}
                  alignItems='center'>
                  <Box>
                    {subTitle1(t('models:teams.subscriptions.subtotal', 'xxs'))}
                    {subTitle2(
                      `${users.length} Team Member${
                        users.length === 1 ? '' : 's'
                      }`,
                      'textSecondary',
                      'xxs'
                    )}
                    {subTitle2(
                      billingCycle === 'year'
                        ? t('models:teams.subscriptions.billedYearly')
                        : t('models:teams.subscriptions.billedMonthly')
                    )}
                  </Box>

                  <Box flexDirection='row' flex={1} justifyContent='flex-end'>
                    <Box>
                      {subTitle1(subtotalLabel, 'xxs')}
                      {billingCycle === 'year' && (
                        <Text
                          color='grey04'
                          textDecorationStyle='solid'
                          textDecorationLine='line-through'
                          mr='xxs'>
                          {originalCostLabel}
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box flexDirection='row' alignItems='center' m='l'>
                  <Box marginRight='xs'>
                    <TouchableOpacity
                      onPress={() => setConfirmEdit(!confirmEdit)}
                      accessibilityLabel={t('shared:confirm')}>
                      <CheckBoxButton checked={confirmEdit} />
                    </TouchableOpacity>
                  </Box>

                  {subTitle1(
                    t(
                      'models:teams.subscriptions.confirmChangeBillingFrequency'
                    )
                  )}
                </Box>
                <Box flexDirection='row' flex={1} mt='l'>
                  <Box flex={1}>
                    <Button
                      fullWidth
                      variant='cancel'
                      onPress={cancelEditPlanPage}>
                      <Text color='black'>{t('shared:cancel')}</Text>
                    </Button>
                  </Box>

                  <Box flex={1}>
                    <Button
                      fullWidth
                      marginLeft='xs'
                      variant='primary'
                      disabled={
                        !confirmEdit ||
                        subscriptionDetails?.subscriptionInterval ===
                          billingCycle ||
                        loading
                      }
                      onPress={changeBillingFrequency}>
                      {loading ? (
                        <ActivityIndicator />
                      ) : (
                        <Text color='background'>
                          {t(
                            'models:teams.subscriptions.changeBillingFrequency'
                          )}
                        </Text>
                      )}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
            {upgradeCTA && pageViewType === viewType.MainPage && (
              <>
                <Box style={{ marginBottom: 4 }}>
                  <Box>
                    {hasSavedPaymentMethod && (
                      <Box>
                        <Box flexDirection='row' mb='m' alignItems='center'>
                          <RadioButton
                            isSelected={
                              !useNewPaymentMethod &&
                              !isSavedPaymentMethodExpired
                            }
                            disabled={isSavedPaymentMethodExpired}
                            onPress={() => {
                              setUseNewPaymentMethod(false);
                            }}
                          />
                          <Text variant='labelSmall' ml='xs'>
                            Saved
                          </Text>
                        </Box>
                        {/*Link payment method*/}
                        {subscriptionDetails &&
                          (subscriptionDetails.paymentCardBrand ?? '').includes(
                            '@'
                          ) && (
                            <Box
                              flexDirection='row'
                              justifyContent='center'
                              alignItems='center'
                              mb='m'
                              width={400}>
                              <Icon
                                name='LinkPayment'
                                variant='l'
                                color='white'
                                marginLeft='m'
                              />
                              <Text
                                variant='labelSmall'
                                color='textSecondary'
                                ml='xxs'>
                                {`Link  ${maskEmail(
                                  subscriptionDetails.paymentCardBrand ?? ''
                                )}`}
                              </Text>
                              <Box flex={1} />
                            </Box>
                          )}
                        {/*stripe payment method*/}
                        {subscriptionDetails?.paymentCardLast4 && (
                          <Box
                            flexDirection='row'
                            justifyContent='center'
                            alignItems='center'
                            mb='m'
                            width={400}>
                            <Icon
                              name='Visa'
                              variant='xxl'
                              color='white'
                              marginLeft='m'
                            />
                            <Text
                              variant='labelSmall'
                              color='textSecondary'
                              ml='m'>
                              **** **** ****{' '}
                              {subscriptionDetails?.paymentCardLast4}
                            </Text>
                            <Text
                              variant='webBodySecondary'
                              color={
                                isSavedPaymentMethodExpired
                                  ? 'alertRed'
                                  : 'textSecondary'
                              }
                              ml='xxl'>
                              {expiration}
                            </Text>
                          </Box>
                        )}

                        <Box flexDirection='row' mb='m' alignItems='center'>
                          <RadioButton
                            disabled={isSavedPaymentMethodExpired}
                            isSelected={useNewPaymentMethod}
                            onPress={() => {
                              setUseNewPaymentMethod(true);
                            }}
                          />
                          <Text variant='labelSmall' ml='xs'>
                            Change payment method
                          </Text>
                        </Box>
                      </Box>
                    )}
                    {useNewPaymentMethod && (
                      <Box mb='l' style={{ minHeight: 400 }}>
                        <WatchedPaymentElement
                          options={stripeOptions}
                          onReady={() => {
                            const xPayment = elements?.getElement('payment');
                            if (xPayment) setPayment(xPayment);
                          }}
                        />
                      </Box>
                    )}

                    {listProducts?.length && (
                      <Box
                        mb='l'
                        flexDirection='row'
                        flex={1}
                        style={{ paddingVertical: 32 }}
                        px='l'
                        borderColor='grey02'
                        borderWidth={1}
                        backgroundColor='grey01'
                        accessibilityLabel={t(
                          'models:teams.subscriptions.subtotal'
                        )}
                        alignItems='center'>
                        <Text variant='labelLarge' mb='xs' color='textPrimary'>
                          {t('models:teams.subscriptions.subtotal')}
                        </Text>

                        <Box
                          flexDirection='row'
                          flex={1}
                          justifyContent='flex-end'>
                          {billingCycle === 'year' && (
                            <Text
                              color='grey04'
                              textDecorationStyle='solid'
                              textDecorationLine='line-through'
                              mr='xxs'>
                              {originalCostLabel}
                            </Text>
                          )}
                          <Text variant='labelLarge' color='textPrimary'>
                            {subtotalLabel}
                          </Text>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box flexDirection='row' flex={1} mt='l'>
                  <Box flex={1}>
                    <Button
                      fullWidth
                      marginLeft='xs'
                      variant='primary'
                      disabled={
                        loading ||
                        !changed() ||
                        // Only require completed payment input when entering
                        // a new payment method — a saved card needs none
                        (useNewPaymentMethod && !values.paymentComplete)
                      }
                      onPress={changePaymentMethod}>
                      {loading ? (
                        <ActivityIndicator />
                      ) : (
                        <Text color='background'>
                          {t('models:teams.subscriptions.upgradeNowCta')}
                        </Text>
                      )}
                    </Button>
                  </Box>
                </Box>
              </>
            )}

            {pageViewType === viewType.EditPaymentMethod && (
              <>
                <Box>
                  {subTitle1(t('models:teams.subscriptions.paymentMethod'))}
                  {subTitle2(
                    t(
                      'models:teams.subscriptions.thisPaymentMethodWillBeBilledForYourSubscription'
                    )
                  )}
                </Box>

                <Box style={{ minHeight: 400 }}>
                  <WatchedPaymentElement
                    options={stripeOptions}
                    onReady={() => {
                      const xPayment = elements?.getElement('payment');
                      if (xPayment) setPayment(xPayment);
                    }}
                  />
                </Box>
              </>
            )}
            {pageViewType === viewType.EditMember && (
              <>
                <Modal
                  animationType='fade'
                  transparent={true}
                  visible={editRoleModalOpen}>
                  <Box style={styles.modalContainer}>
                    <TouchableWithoutFeedback
                      onPress={() => setEditRoleModalOpen(!editRoleModalOpen)}
                      style={styles.modal}
                      accessibilityLabel='CloseModalButton'>
                      <Box style={styles.modal} />
                    </TouchableWithoutFeedback>

                    {TeamMemberRoles && (
                      <ShadowBox variant='thin' style={styles.popUpWeb}>
                        <Box
                          marginVertical='m'
                          marginHorizontal='m'
                          flex={1}
                          justifyContent='space-between'>
                          <Box
                            style={styles.modalHeader}
                            accessibilityLabel='RoleSelectModal'>
                            <ModalHeader
                              title={t('models:projects.roles.selectRole')}
                              submitLabel='Cancel'
                              onCancel={() => setEditRoleModalOpen(false)}
                              showCancel={false}
                              onSubmit={() => setEditRoleModalOpen(false)}
                            />
                          </Box>
                          {TeamMemberRoles.map((i, index) => {
                            return (
                              <TouchableOpacity
                                key={i}
                                onPress={() => {
                                  updateTeamMemberRole(selectedMember!.id, i);
                                  setEditRoleModalOpen(false);
                                }}
                                accessibilityLabel={i}>
                                <Box
                                  flexDirection='row'
                                  justifyContent='space-between'
                                  alignItems='center'
                                  marginBottom={
                                    index !== TeamMemberRoles.length - 1
                                      ? 'l'
                                      : undefined
                                  }>
                                  <Text
                                    variant='body'
                                    style={{ textTransform: 'capitalize' }}>
                                    {i.toLowerCase()}
                                  </Text>
                                  {i.toLowerCase() ===
                                    selectedMemberRole?.toLowerCase() && (
                                    <Icon
                                      variant='s'
                                      name='Check'
                                      color='greenSecondary'
                                      accessibilityLabel={`SelectedRole-${i}`}
                                    />
                                  )}
                                </Box>
                              </TouchableOpacity>
                            );
                          })}
                          <>
                            <Box
                              backgroundColor='grey02'
                              height={1}
                              marginVertical='m'
                            />
                            <TouchableOpacity
                              onPress={() => {
                                setEditRoleModalOpen(false);
                                removeMemberFromTeam(selectedMember!.id);
                              }}
                              accessibilityLabel='Remove'>
                              <Text variant='body' color='alertRed'>
                                {t('models:editMembers.removeMember.title')}
                              </Text>
                            </TouchableOpacity>
                          </>
                        </Box>
                      </ShadowBox>
                    )}
                  </Box>
                </Modal>
                <Box>
                  {subTitle1(t('models:teams.subscriptions.teamMembers'))}
                  {subTitle2(
                    t('models:teams.subscriptions.youWillBeBilledForEachSeat')
                  )}
                </Box>
                <Box mb='s'>
                  <Box flexDirection='row' marginBottom='xs'>
                    <Text variant='labelSmall' color='textPrimary'>
                      {t('models:contacts.members')}
                    </Text>
                    <Box flex={1}></Box>
                    <TouchableOpacity
                      onPress={() => {
                        navigator.navigate('add-team-members');
                      }}>
                      <Text variant='labelSmall' color='greenSecondary'>
                        {t('models:teams.subscriptions.addMember')}
                      </Text>
                    </TouchableOpacity>
                  </Box>
                  <Box mt='xs'>
                    {sortedUsers.map((user) => {
                      if (!user) return;
                      const { id: memberId, name, avatar, role } = user;

                      return (
                        <Box
                          flex={1}
                          key={memberId}
                          py='xs'
                          px='m'
                          borderBottomWidth={1}
                          borderBottomColor='grey02'>
                          <TouchableOpacity
                            onPress={() => {
                              if (role !== TeamRole.Owner) {
                                setSelectedMemberRole(role);
                                setSelectedMember(user);
                                setEditRoleModalOpen(true);
                              }
                            }}
                            accessibilityLabel='MemberCard'>
                            <Box flexDirection='row' alignItems='center'>
                              <Avatar
                                id={memberId}
                                avatar={avatar}
                                label={name}
                                isMultiple={false}
                                size='medium'
                              />
                              <Box
                                style={{
                                  marginLeft:
                                    theme.spacing.xs + theme.spacing.xxxs,
                                }}
                                flex={1}>
                                <Text
                                  variant='webBodySecondary'
                                  numberOfLines={0}>
                                  {name}
                                </Text>
                              </Box>

                              <Text
                                variant='webBodySecondary'
                                marginLeft='s'
                                color='textPrimary'
                                style={{ textTransform: 'capitalize' }}>
                                {role?.toLowerCase()}
                              </Text>

                              {role !== TeamRole.Owner && (
                                <Icon
                                  name='ChevronDown'
                                  color='textPrimary'
                                  marginLeft='m'
                                  accessibilityLabel='RoleDropDown'
                                />
                              )}
                              {role === TeamRole.Owner && (
                                <Box width={40}></Box>
                              )}
                            </Box>
                          </TouchableOpacity>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Box
                  mt='xs'
                  mb='l'
                  flexDirection='row'
                  flex={1}
                  style={{ paddingTop: 22, paddingBottom: 4 }}
                  px='l'
                  borderColor='grey02'
                  borderWidth={1}
                  backgroundColor='grey01'
                  accessibilityLabel={t('models:teams.subscriptions.subtotal')}
                  alignItems='center'>
                  <Box>
                    {subTitle1(
                      t('models:teams.subscriptions.previousBillingAmount'),
                      'xxs'
                    )}
                    {subTitle2(
                      `${team?.users.length} Team Member${
                        team?.users.length === 1 ? '' : 's'
                      }`,
                      'textSecondary',
                      'xxs'
                    )}
                    {subTitle2(
                      billingCycle === 'year'
                        ? t('models:teams.subscriptions.chargedYearly')
                        : t('models:teams.subscriptions.chargedMonthly')
                    )}
                  </Box>

                  <Box flexDirection='row' flex={1} justifyContent='flex-end'>
                    <Box>{subTitle1(previousMembersCost(), 'xxs')}</Box>
                  </Box>
                </Box>

                <Box
                  mb='l'
                  flexDirection='row'
                  flex={1}
                  style={{ paddingTop: 22, paddingBottom: 4 }}
                  px='l'
                  borderColor='grey02'
                  borderWidth={1}
                  backgroundColor='grey01'
                  accessibilityLabel={t('models:teams.subscriptions.subtotal')}
                  alignItems='center'>
                  <Box>
                    {subTitle1(
                      t('models:teams.subscriptions.newBillingAmount'),
                      'xxs'
                    )}
                    {subTitle2(
                      `${users.length} Team Member${
                        users.length === 1 ? '' : 's'
                      }`,
                      'textSecondary',
                      'xxs'
                    )}
                    {subTitle2(
                      billingCycle === 'year'
                        ? t('models:teams.subscriptions.chargedYearly')
                        : t('models:teams.subscriptions.chargedMonthly')
                    )}
                  </Box>

                  <Box flexDirection='row' flex={1} justifyContent='flex-end'>
                    <Box>{subTitle1(newMembersCost(), 'xxs')}</Box>
                  </Box>
                </Box>
                <Box flexDirection='row' alignItems='center' m='l'>
                  <Box marginRight='xs'>
                    <TouchableOpacity
                      onPress={() => setConfirmEdit(!confirmEdit)}
                      accessibilityLabel={t('shared:confirm')}>
                      <CheckBoxButton checked={confirmEdit} />
                    </TouchableOpacity>
                  </Box>

                  {subTitle1(
                    t('models:teams.subscriptions.confirmSeatChangesMsg'),
                    'none'
                  )}
                </Box>
                <Box flexDirection='row' flex={1} mt='l'>
                  <Box flex={1}>
                    <Button
                      fullWidth
                      variant='cancel'
                      onPress={cancelEditMemberPage}>
                      <Text color='black'>{t('shared:cancel')}</Text>
                    </Button>
                  </Box>

                  <Box flex={1}>
                    <Button
                      fullWidth
                      marginLeft='xs'
                      variant='primary'
                      disabled={loading || !confirmEdit}
                      onPress={changeSeat}>
                      {loading ? (
                        <ActivityIndicator />
                      ) : (
                        <Text color='background'>
                          {t('models:teams.subscriptions.confirmSeatChanges')}
                        </Text>
                      )}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </ScrollView>
        {pageViewType === viewType.EditPaymentMethod && (
          <Box backgroundColor='white' paddingHorizontal='m' paddingBottom='m'>
            <Box flexDirection='row' alignItems='center' mb='s'>
              <Box marginRight='xs'>
                <TouchableOpacity
                  onPress={() => setConfirmEdit(!confirmEdit)}
                  accessibilityLabel={t('shared:confirm')}>
                  <CheckBoxButton checked={confirmEdit} />
                </TouchableOpacity>
              </Box>
              {subTitle1(
                t('models:teams.subscriptions.confirmChangePaymentMethod')
              )}
            </Box>
            <Box flexDirection='row'>
              <Box flex={1}>
                <Button
                  fullWidth
                  variant='cancel'
                  onPress={cancelPaymentMethodPage}>
                  <Text color='black'>{t('shared:cancel')}</Text>
                </Button>
              </Box>
              <Box flex={1}>
                <Button
                  fullWidth
                  marginLeft='xs'
                  variant='primary'
                  disabled={loading || !confirmEdit || !values.paymentComplete}
                  onPress={changePaymentMethod}>
                  {loading ? (
                    <ActivityIndicator />
                  ) : (
                    <Text color='background'>
                      {t('models:teams.subscriptions.changePaymentMethod')}
                    </Text>
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
const styles = StyleSheet.create({
  modal: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
    opacity: 0.5,
  },
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalHeader: {
    paddingBottom: theme.spacing.xl,
    paddingTop: theme.spacing.l,
  },
  popUpWeb: {
    position: 'absolute',
    backgroundColor: 'white',
    width: 300,
    paddingBottom: theme.spacing.l,
    borderRadius: theme.borderRadii.s,
  },
});
